import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LiveHelpOutlinedIcon from '@mui/icons-material/LiveHelpOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import { alpha, AppBar, ButtonBase, IconButton, Stack, Toolbar, Typography } from '@mui/material';
import { ProductGroups, Products, Routes } from 'enums';
import { getCoPilotTabRoutes, isCoPilotPath, isRecruitingCoPilotPath } from 'features/co-pilot/co-pilot-routes';
import NotificationBell from 'features/engagement/notifications/notification-bell';
import FaqDialog from 'features/onboarding/faq/faq-dialog';
import { FaqItemKey } from 'features/onboarding/faq/faq-dialog.types';
import PaiTooltip from 'features/ui/base-components/pai-tooltip';
import { useExitSudo } from 'hooks/use-exit-sudo';
import { useLicenseStatus } from 'hooks/use-license-status';
import { useUserData } from 'hooks/use-user-data';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { NavigationStates } from 'stores/app-bar-navigation-store';
import useWizardCrumbStore from 'stores/wizard-crumb-store';
import { makeStyles } from 'tss-react/mui';
import AdminReloginDialog from '../sidebar/admin-relogin-dialog';
import { getActivePanelItem, getActiveSection, TOP_BAR_HEIGHT, useNavSections } from '../rail/nav-config';
import LinkedinConnectionBanner from './linkedin-connection-banner';
import PwaInstallButton from './pwa-install-button';
import TaskManagerNavigation from './task-manager-navigation';

interface Props {
  /** Left edge of the content area (rail + optional context panel). */
  offsetLeft: number;
  /** Shown when the active section has a panel — lets the user collapse/expand it. */
  showPanelToggle: boolean;

  togglePanel(): void;
}

const TopBar: React.FC<Props> = ({ offsetLeft, showPanelToggle, togglePanel }) => {
  const { classes } = useStyles({ offsetLeft });
  const { t } = useTranslation();
  const location = useLocation();
  const { email: userEmail } = useUserData();

  const sections = useNavSections();
  const activeSection = getActiveSection(sections, location.pathname);
  const activeItem = getActivePanelItem(activeSection, location.pathname);

  const isAdminLogin = !!localStorage.getItem('adminEmail');
  const [adminReloginDialogOpen, setAdminReloginDialogOpen] = useState(false);
  const { exitImpersonation } = useExitSudo();

  const [faqDialog, setFaqDialog] = useState<{ open: boolean; activeFaq: FaqItemKey }>({
    open: false,
    activeFaq: FaqItemKey.SYNCHRONIZING_YOUR_ACCOUNT,
  });

  const wizardCrumbName = useWizardCrumbStore(state => state.campaignName);
  const isNewCampaignWizard = location.pathname.startsWith(Routes.RECRUITING_BOOSTER_CO_PILOT_NEW_CAMPAIGN);
  const onCoPilotPage = isCoPilotPath(location.pathname);
  const coPilotProductGroup = isRecruitingCoPilotPath(location.pathname) ? ProductGroups.HR_BOOSTER : ProductGroups.LEAD_BOOSTER;
  const coPilotRoutes = getCoPilotTabRoutes(coPilotProductGroup);
  // Außerhalb von Recruiting/Sales führt der LinkedIn-Hinweis in den Bereich, für den es eine Lizenz gibt.
  const hasSalesLicense = useLicenseStatus(Products.SAIA_SALES) !== 'none';
  const accountsRouteGroup = onCoPilotPage ? coPilotProductGroup : hasSalesLicense ? ProductGroups.LEAD_BOOSTER : ProductGroups.HR_BOOSTER;
  const accountsRoute = getCoPilotTabRoutes(accountsRouteGroup)[NavigationStates.LINKEDIN_ACCOUNT_MANAGEMENT];

  const handleOpenAdminReloginDialog = async () => {
    // Same contract as before: return to the admin's own account, only ask
    // for the password if that exchange fails.
    const restored = await exitImpersonation();
    if (restored) {
      window.location.reload();
      return;
    }
    setAdminReloginDialogOpen(true);
  };

  return (
    <>
      <AppBar className={classes.appBar} position="fixed" elevation={0}>
        <Toolbar className={classes.toolBar}>
          {showPanelToggle ? (
            <ButtonBase className={classes.menuButton} onClick={togglePanel}>
              <MenuIcon className={classes.menuIcon} />
            </ButtonBase>
          ) : null}

          {/* Breadcrumb: section > active page */}
          <Stack direction="row" alignItems="center" gap={1} className={classes.breadcrumb}>
            {activeSection ? (
              <>
                <Typography className={classes.breadcrumbSection}>{activeSection.label}</Typography>
                {activeItem ? (
                  <>
                    <ChevronRightIcon className={classes.breadcrumbChevron} />
                    <Typography className={classes.breadcrumbPage}>{isNewCampaignWizard ? (wizardCrumbName ?? t('wizBreadcrumbNew')) : activeItem.text}</Typography>
                  </>
                ) : null}
              </>
            ) : (
              <Typography className={classes.breadcrumbSection}>SAIA</Typography>
            )}
          </Stack>

          <LinkedinConnectionBanner accountsRoute={accountsRoute} />

          <Stack direction="row" flexGrow={1} />

          <PwaInstallButton />

          {/* Mitteilungen (Engagement Center) — always available, also during admin impersonation. */}
          <NotificationBell />

          {isAdminLogin ? (
            <Stack className={classes.adminLogin} direction="row" alignItems="center" gap={2}>
              <SupervisedUserCircleIcon className={classes.adminLoginIcon} />
              <Typography className={classes.adminLoginText}>{t('adminLoginAs', { userEmail })}</Typography>
              <PaiTooltip title={t('backToMyAccount')} placement="bottom-end">
                <IconButton className={classes.adminLogoutButton} onClick={handleOpenAdminReloginDialog}>
                  <LogoutIcon />
                </IconButton>
              </PaiTooltip>
            </Stack>
          ) : onCoPilotPage ? (
            <Stack direction="row" alignItems="center" gap={2}>
              <TaskManagerNavigation navigationState={NavigationStates.TASK_MANAGER} route={coPilotRoutes[NavigationStates.TASK_MANAGER]} />
              <PaiTooltip title={t('frequentlyAskedQuestions')}>
                <IconButton size="small" onClick={() => setFaqDialog((prev) => ({ ...prev, open: true }))} className={classes.helpIcon}>
                  <LiveHelpOutlinedIcon />
                </IconButton>
              </PaiTooltip>
            </Stack>
          ) : null}
        </Toolbar>
      </AppBar>

      <FaqDialog
        open={faqDialog.open}
        activeFaq={faqDialog.activeFaq}
        productGroup={coPilotProductGroup}
        setActiveFaq={(faq) => () => setFaqDialog((prev) => ({ ...prev, activeFaq: faq }))}
        handleClose={() => setFaqDialog({ open: false, activeFaq: FaqItemKey.SYNCHRONIZING_YOUR_ACCOUNT })}
      />
      <AdminReloginDialog open={adminReloginDialogOpen} handleClose={() => setAdminReloginDialogOpen(false)} />
    </>
  );
};

const useStyles = makeStyles<{ offsetLeft: number }>()((theme, { offsetLeft }) => ({
  appBar: {
    background: '#FFF',
    height: `${TOP_BAR_HEIGHT}px !important`,
    zIndex: 1100,
  },
  toolBar: {
    minHeight: `${TOP_BAR_HEIGHT}px !important`,
    paddingLeft: `calc(20px + ${offsetLeft}px) !important`,
    paddingRight: '20px !important',
    transition: 'padding-left 225ms',
    background: '#FFF',
    color: theme.palette.text.primary,
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
    gap: '12px',
  },
  menuButton: {
    borderRadius: '6px',
    padding: '4px',
  },
  menuIcon: {
    fontSize: theme.typography.pxToRem(22),
    color: '#555',
  },
  breadcrumb: {
    minWidth: 0,
  },
  breadcrumbSection: {
    fontSize: theme.typography.pxToRem(13),
    color: '#707070',
    whiteSpace: 'nowrap',
  },
  breadcrumbChevron: {
    fontSize: theme.typography.pxToRem(14),
    color: '#707070',
  },
  breadcrumbPage: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 700,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  helpIcon: {
    color: theme.palette.primary.main,
  },
  adminLogin: {
    height: 'calc(100% - 16px)',
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
    color: '#FFF',
    padding: '4px 8px 4px 12px',
    borderRadius: '4px',
  },
  adminLoginText: {
    fontSize: theme.typography.pxToRem(12),
  },
  adminLoginIcon: {
    fontSize: theme.typography.pxToRem(18),
  },
  adminLogoutButton: {
    height: '100%',
    width: '35px',
    backgroundColor: alpha('#FFF', 0.2),
    color: '#FFF',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: alpha('#000', 0.2),
    },
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(20),
      color: '#FFF',
    },
  },
}));

export default TopBar;
